import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';
import { FormAutocompleteField, LocalizedText } from '@mews/framework';
import { Button, debounce, DebounceTimeoutValues, DropdownItem, IconName, StackItem } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { Loader, TooltippedIcon } from '@mews-commander/ui';
import { CompaniesByKey } from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import { useCompaniesKeySearch } from '../../../../../queries';
import { WithEnterpriseIds } from '../../../../../types';
import { getEnterprisesUniqChainIds } from '../../../../../selectors';

interface CompanyKeySelectFieldProps extends WithEnterpriseIds {
    disabled: boolean;
}

const renderCompanyItems = (companies?: Array<CompaniesByKey> | null) =>
    companies?.map(({ key }) => <DropdownItem key={key} value={key} label={key} />);

export const CompanyKeySelectField = ({ disabled, enterpriseIds }: CompanyKeySelectFieldProps) => {
    const value = useWatch({ name: 'companyKey' });
    const [searchTerm, setSearchTerm] = useState(value);
    const chainIds = useSelector((state: ApplicationState) =>
        getEnterprisesUniqChainIds(state, { ids: enterpriseIds })
    );
    const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useCompaniesKeySearch(
        searchTerm,
        chainIds
    );

    const debouncedSetSearchTerm = useMemo(
        () =>
            debounce((searchValue: string) => {
                setSearchTerm(searchValue);
            }, DebounceTimeoutValues.textInput),
        []
    );

    const onCompanyKeyChange = (searchValue: string) => {
        // Immediate items cleanup if no search value
        if (!searchValue) {
            return setSearchTerm(searchValue);
        }

        debouncedSetSearchTerm(searchValue);
    };

    const handleLoadMoreClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (isFetching || isFetchingNextPage) return;

        void fetchNextPage();
    };

    const tooltipTextKey =
        enterpriseIds.length > 1
            ? TextKey.VoucherCompanyKeyFieldHelperTextMultipleEnterprises
            : TextKey.VoucherCompanyKeyFieldHelperTextSingleEnterprise;

    return (
        <FormAutocompleteField
            name="companyKey"
            disabled={disabled}
            label={<LocalizedText textKey={TextKey.CompanyKey} />}
            onSearchChange={onCompanyKeyChange}
            captionIconWithTooltip={<TooltippedIcon size="small" value={IconName.Info} tooltip={tooltipTextKey} />}
            value={value}
            virtualized
        >
            {data?.pages?.map(({ companiesByKeys }) => renderCompanyItems(companiesByKeys))}
            {hasNextPage ? (
                <StackItem padding="100">
                    <Button variant="ghost" onClick={handleLoadMoreClick} wide type="submit">
                        {isFetchingNextPage ? <Loader size="small" /> : <LocalizedText textKey={TextKey.More} />}
                    </Button>
                </StackItem>
            ) : null}
        </FormAutocompleteField>
    );
};
